import { useEffect } from "react";
import { ApiError, useFacets, useHealth } from "../lib/api";
import { currentPath, loginPageHref } from "../lib/auth";

/**
 * The session gate. Every /api route except health needs a Cloudflare Access session and a Plenum
 * membership (PRD 14.4); the facets probe is the first call every page makes, so its failure is the
 * signal.
 *
 * - Access itself turned the call away (no session): send the visitor to /login, keeping the page
 *   they asked for. This is the ordinary sign-in path.
 * - The Worker answered 401: Access let the call through but the Worker found no valid assertion,
 *   which means the Worker's Access secrets are wrong or missing. Say so; sending the visitor round
 *   the login again would loop.
 * - The Worker answered 403: signed in, but the email belongs to no Plenum organization.
 *
 * Hidden once a session works.
 */
export function SessionBanner() {
  const facets = useFacets();
  const health = useHealth();
  const err = facets.error instanceof ApiError ? facets.error : null;
  const needsLogin = err?.status === 401 && err.code === "access_login";

  useEffect(() => {
    if (needsLogin) window.location.replace(loginPageHref(currentPath()));
  }, [needsLogin]);

  if (!err || (err.status !== 401 && err.status !== 403)) return null;

  if (needsLogin) {
    return (
      <output
        className="block border-b px-3 py-1.5 text-[12.5px]"
        style={{ background: "var(--panel-2)", borderColor: "var(--line)", color: "var(--muted)" }}
        data-testid="session-redirect"
      >
        Taking you to sign in…
      </output>
    );
  }

  const forbidden = err.status === 403;
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-3 py-1.5 text-[12.5px]"
      style={{ background: "var(--panel-2)", borderColor: "var(--gold)", color: "var(--paper)" }}
      data-testid="session-banner"
    >
      <span className="font-semibold" style={{ color: "var(--gold)" }}>
        {forbidden ? "No membership yet." : "Session not accepted."}
      </span>
      <span>
        {forbidden
          ? "You signed in, but this email belongs to no Plenum organization, so the map, tables and charts stay empty. Ask Marc to add it, then sign out and back in."
          : "Cloudflare Access let the request through, but the Worker found no valid Access assertion, so the map, tables and charts stay empty. Usually the Worker is missing its Access secrets (runbook, first deploy checklist, step 3)."}
      </span>
      {forbidden && health.data?.logout_url ? (
        <a
          className="btn btn-ghost btn-sm"
          href={health.data.logout_url}
          data-testid="banner-sign-out"
        >
          Sign out
        </a>
      ) : (
        <a
          className="btn btn-ghost btn-sm"
          href={loginPageHref(currentPath())}
          data-testid="banner-sign-in"
        >
          Sign in again
        </a>
      )}
    </div>
  );
}

const REPO = "https://github.com/mjohnson518/powerplants";

/** Opens a prefilled GitHub issue with the current page and build, for feedback while testing. */
export function FeedbackLink({ version, layer }: { version?: string; layer?: string | null }) {
  const href = () => {
    const page = typeof window === "undefined" ? "" : window.location.href;
    const title = encodeURIComponent(`Feedback: ${document.title || "Plenum"}`);
    const body = encodeURIComponent(
      [
        `Page: ${page}`,
        `Build: ${version ?? "unknown"} · layer ${layer ?? "—"}`,
        `Browser: ${typeof navigator === "undefined" ? "" : navigator.userAgent}`,
        "",
        "What I expected:",
        "",
        "What happened:",
        "",
      ].join("\n"),
    );
    return `${REPO}/issues/new?title=${title}&body=${body}&labels=feedback`;
  };
  return (
    <a
      className="btn btn-ghost btn-sm"
      href={href()}
      onClick={(e) => {
        // compute at click time so the URL reflects the page the tester is on
        e.currentTarget.href = href();
      }}
      target="_blank"
      rel="noreferrer"
      data-testid="feedback-link"
    >
      Feedback
    </a>
  );
}
